import React from "react";
import { Box, Paper, Skeleton } from "@mui/material";

// ─── Slider track shimmer ─────────────────────────────────────────────────────
// Mimics the MUI Slider: a thin rail with a filled track portion and thumb(s)

interface ShimmerSliderProps {
  /** 0–1 fraction where the single thumb sits (for single-handle slider) */
  thumbPosition?: number;
  /** For range slider: [left, right] fractions */
  rangePositions?: [number, number];
}

const ShimmerSlider: React.FC<ShimmerSliderProps> = ({
  thumbPosition,
  rangePositions,
}) => {
  const isRange = rangePositions !== undefined;
  const leftFrac = isRange ? rangePositions![0] : 0;
  const rightFrac = isRange ? rangePositions![1] : thumbPosition ?? 0.6;

  return (
    <Box sx={{ position: "relative", height: 22, mx: 1 }}>
      {/* Rail — full width grey bar */}
      <Skeleton
        variant="rectangular"
        width="100%"
        height={6}
        animation="wave"
        sx={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          borderRadius: 3,
          bgcolor: "grey.200",
        }}
      />

      {/* Track — filled portion between left and right positions */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          left: `${leftFrac * 100}%`,
          width: `${(rightFrac - leftFrac) * 100}%`,
          height: 6,
          borderRadius: 3,
          bgcolor: "primary.main",
          opacity: 0.25,
        }}
      />

      {/* Left thumb (range) or single thumb */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: `${leftFrac * 100}%`,
          transform: "translate(-50%, -50%)",
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: "white",
          border: "3px solid",
          borderColor: "grey.300",
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        }}
      />

      {/* Right thumb (range slider only) */}
      {isRange && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: `${rightFrac * 100}%`,
            transform: "translate(-50%, -50%)",
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: "white",
            border: "3px solid",
            borderColor: "grey.300",
            boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          }}
        />
      )}
    </Box>
  );
};

// ─── Value display box shimmer ────────────────────────────────────────────────
// Mimics the bordered pill boxes showing ₹15,00,000 etc.

const ShimmerValueBox: React.FC<{ width?: number }> = ({ width = 160 }) => (
  <Skeleton
    variant="rectangular"
    width={width}
    height={44}
    animation="wave"
    sx={{
      borderRadius: 2.5,
      bgcolor: "grey.100",
      border: "1.5px solid",
      borderColor: "grey.200",
    }}
  />
);

// ─── main skeleton ────────────────────────────────────────────────────────────

const Step3LoanDetailsSkeleton: React.FC = () => (
  <Box>
    {/* ── Section header ── */}
    <Box sx={{ mb: 4 }}>
      {/* "✦ Loan Details" breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Skeleton
          variant="rectangular"
          width={13}
          height={13}
          animation="wave"
          sx={{ borderRadius: 0.5 }}
        />
        <Skeleton
          variant="rectangular"
          width={90}
          height={13}
          animation="wave"
          sx={{ borderRadius: 1 }}
        />
      </Box>

      {/* "Let's secure your offer" h3 */}
      <Skeleton
        variant="rectangular"
        width="42%"
        height={38}
        animation="wave"
        sx={{ borderRadius: 1.5, mb: 1.5 }}
      />

      {/* Subtitle */}
      <Skeleton
        variant="rectangular"
        width="65%"
        height={14}
        animation="wave"
        sx={{ borderRadius: 1 }}
      />
    </Box>

    {/* ── Form card ── */}
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 4 },
        borderRadius: 3,
        bgcolor: "#fff",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: 200,
          height: 200,
          borderRadius: "0 0 0 100%",
          bgcolor: "rgba(27,107,90,0.04)",
        },
      }}
    >
      {/* ── Badge chips — top right ── */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 4,
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        {/* "100% Financing Available" chip */}
        <Skeleton
          variant="rectangular"
          width={190}
          height={28}
          animation="wave"
          sx={{ borderRadius: 3, bgcolor: "#FFF8EC" }}
        />
        {/* "Instant Approval" chip */}
        <Skeleton
          variant="rectangular"
          width={140}
          height={28}
          animation="wave"
          sx={{ borderRadius: 3, bgcolor: "rgba(27,107,90,0.08)" }}
        />
      </Box>

      {/* ── Loan Amount section ── */}
      <Box sx={{ mb: 5 }}>
        {/* Section heading */}
        <Skeleton
          variant="rectangular"
          width={200}
          height={20}
          animation="wave"
          sx={{ borderRadius: 1, mb: 3 }}
        />

        {/* Two value boxes with "to" separator */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <ShimmerValueBox width={160} />
          <Skeleton
            variant="text"
            width={20}
            height={20}
            animation="wave"
            sx={{ borderRadius: 1 }}
          />
          <ShimmerValueBox width={160} />
        </Box>

        {/* Range slider track */}
        <Box sx={{ px: { xs: 1, md: 2 }, mb: 1 }}>
          <ShimmerSlider rangePositions={[0.28, 0.47]} />
        </Box>

        {/* ₹1 L — ₹50 L range labels */}
        <Box sx={{ display: "flex", justifyContent: "space-between", px: { xs: 1, md: 2 } }}>
          <Skeleton variant="text" width={30} height={14} animation="wave" sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={34} height={14} animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
      </Box>

      {/* ── Total Education Cost section ── */}
      <Box sx={{ mb: 5 }}>
        {/* Section heading */}
        <Skeleton
          variant="rectangular"
          width={280}
          height={20}
          animation="wave"
          sx={{ borderRadius: 1, mb: 3 }}
        />

        {/* Single centred value box */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <ShimmerValueBox width={180} />
        </Box>

        {/* Single-handle slider track */}
        <Box sx={{ px: { xs: 1, md: 2 }, mb: 1 }}>
          <ShimmerSlider thumbPosition={0.6} />
        </Box>

        {/* ₹1 L — ₹50 L range labels */}
        <Box sx={{ display: "flex", justifyContent: "space-between", px: { xs: 1, md: 2 } }}>
          <Skeleton variant="text" width={30} height={14} animation="wave" sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width={34} height={14} animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
      </Box>

      {/* ── Action buttons — bottom right ── */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {/* Back */}
        <Skeleton
          variant="rectangular"
          width={100}
          height={50}
          animation="wave"
          sx={{ borderRadius: 2, bgcolor: "grey.200" }}
        />
        {/* Submit Application */}
        <Skeleton
          variant="rectangular"
          width={200}
          height={50}
          animation="wave"
          sx={{ borderRadius: 2, bgcolor: "grey.300" }}
        />
      </Box>
    </Paper>
  </Box>
);

export default Step3LoanDetailsSkeleton;